import {
    Box,
    Button,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useContext, useState } from "react";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { isBrowser } from "react-device-detect";
import useFetch from "../../hooks/useFetch";
import { Loading } from "../../components/Loading";
import { ServerProblem } from "../ReRoute/ServerProblem";
import { UserContext } from "../../context/UserContext";
import { ReceivingFacility } from "../../interfaces/ReceivingFacility";
import ReceivingFacilityService from "../../services/ReceivingFacilityService";
import { ReceivingFacilityDialog } from "./ReceivingFacilityDialog";

export const ReceivingFacilities = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [cookies] = useCookies(["access_token"]);
    const userCtx = useContext(UserContext);
    const canEdit = userCtx?.user?.role?.roletype === "orgadmin";
    const fontVariant = isBrowser ? "body2" : "caption";

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selected, setSelected] = useState<ReceivingFacility | undefined>(undefined);

    const { data, loading, error, refresh } = useFetch({
        url: ReceivingFacilityService.listUrl(),
        access_token: cookies.access_token,
    });

    const openDialog = (facility?: ReceivingFacility) => {
        setSelected(facility);
        setDialogOpen(true);
    };

    const handleSaved = () => {
        setDialogOpen(false);
        refresh();
    };

    if (error) return <ServerProblem />;
    if (loading || !data) return <Loading />;

    const facilities: ReceivingFacility[] = data;
    const headerCellSx = { padding: "16px 6px" };

    return (
        <Box sx={{ px: 3, py: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, px: 1 }}>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                    {t("receiving_facilities")}
                </Typography>
                {canEdit && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>
                        {t("rf_add_facility")}
                    </Button>
                )}
            </Box>

            <TableContainer component={Paper}>
                <Table aria-label={t("receiving_facilities")}>
                    <TableHead>
                        <TableRow
                            sx={{
                                bgcolor: theme.palette.primary.main,
                                "& th": { color: theme.palette.text.secondary },
                            }}
                        >
                            <TableCell sx={headerCellSx}>{t("rf_name")}</TableCell>
                            <TableCell sx={headerCellSx}>{t("rf_address")}</TableCell>
                            <TableCell sx={headerCellSx}>{t("rf_notification_methods")}</TableCell>
                            <TableCell sx={headerCellSx}>{t("rf_primary_contact")}</TableCell>
                            <TableCell sx={headerCellSx}>{t("rf_contacts")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {facilities.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Typography variant={fontVariant}>{t("rf_no_facilities")}</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {facilities.map((facility) => {
                            const primary = [...facility.contacts].sort((a, b) => a.priority - b.priority)[0];
                            return (
                                <TableRow
                                    key={facility.id}
                                    hover
                                    onClick={() => openDialog(facility)}
                                    sx={{
                                        cursor: "pointer",
                                        "&:nth-of-type(odd)": { background: theme.palette.info.dark },
                                        "&:nth-of-type(even)": { background: theme.palette.info.light },
                                    }}
                                >
                                    <TableCell sx={headerCellSx}>
                                        <Typography variant={fontVariant}>{facility.name}</Typography>
                                    </TableCell>
                                    <TableCell sx={headerCellSx}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            {facility.address_verified && (
                                                <CheckCircleIcon color="success" fontSize="small" />
                                            )}
                                            <Typography variant={fontVariant}>
                                                {facility.display_address || facility.address}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={headerCellSx}>
                                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                            {facility.notification_methods.map((method) => (
                                                <Chip key={method} size="small" label={t(`rf_method_${method}`)} />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={headerCellSx}>
                                        <Typography variant={fontVariant}>
                                            {primary ? `${primary.name} (${primary.phone})` : "-"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={headerCellSx}>
                                        <Typography variant={fontVariant}>{facility.contacts.length}</Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {dialogOpen && (
                <ReceivingFacilityDialog
                    open={dialogOpen}
                    facility={selected}
                    readOnly={!canEdit}
                    onClose={() => setDialogOpen(false)}
                    onSaved={handleSaved}
                />
            )}
        </Box>
    );
};
